<template>
  <section class="users">
    <Header>Пользователи</Header>
    <div class="users__main">
      <div class="tools">
        <Search placeholder="Поиск" v-model="searchQuery" class="tool" />
        <h2 class="users__heading">Фильтры</h2>
        <div class="tools__filters">
          <SelectButtons
            :items="roleFilterItems"
            v-model="pagination.options.role"
          />
          <SelectButtons
            :items="verifiedDropdownItems"
            v-model="pagination.options.verified"
          />
        </div>
        <h2 class="users__heading">Статистика</h2>
        <div class="users__stats stats">
          <div class="stats__item">
            <p class="stats__title">
              {{ numberOfUsers }}
            </p>
            <p class="stats__subtitle">пользователей</p>
          </div>
          <div
            class="stats__item stats__item--clickable"
            @click="openActiveUsersModal"
          >
            <p class="stats__title">
              {{ numberOfUsersOnline }}
            </p>
            <p class="stats__subtitle">активных сессий</p>
          </div>
        </div>
      </div>
      <div class="users__list">
        <div class="users__loading" v-if="isLoading">
          <LoadingLogo />
        </div>
        <div class="users__empty" v-else-if="!users.length">
          <h3>Пользователи не найдены</h3>
        </div>
        <div class="users-table" v-else>
          <Table>
            <table>
              <thead>
                <th>Фото</th>
                <th>Имя</th>
                <th>Почта</th>
                <th>
                  <button
                    :class="[
                      'table-sortable',
                      {
                        'table-sortable-desc':
                          pagination.options.order == 'DESC',
                      },
                      {
                        'table-sortable-asc': pagination.options.order == 'ASC',
                      },
                    ]"
                    @click="toggleSort()"
                  >
                    Дата регистрации
                  </button>
                </th>
                <th>Действия</th>
              </thead>
              <tbody>
                <h3
                  style="white-space: nowrap"
                  v-if="
                    !searchUserLoading && !filteredUsers.length && searchQuery
                  "
                >
                  Пользователей не найдено
                </h3>
                <tr class="loading" v-if="searchUserLoading">
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr
                  v-for="user in filteredUsers"
                  :key="user.uuid"
                  @click="selectUser(user)"
                >
                  <td>
                    <div
                      v-if="user.avatar_is_loading"
                      class="table-cover-loading table-center"
                    ></div>
                    <Transition name="resize">
                      <div
                        class="table-cover table-center"
                        v-show="
                          file(user.profile?.avatar_url)?.base64 &&
                          !user.avatar_is_loading
                        "
                      >
                        <img
                          :src="file(user.profile?.avatar_url)?.base64"
                          alt="Avatar"
                        />
                      </div>
                    </Transition>
                    <Transition name="resize">
                      <div
                        class="table-cover table-center"
                        v-show="
                          !file(user.profile?.avatar_url)?.base64 &&
                          !user.avatar_is_loading
                        "
                      >
                        <img
                          :src="require('@/assets/placeholder.png')"
                          alt="Avatar Placeholder"
                        />
                      </div>
                    </Transition>
                  </td>
                  <td>
                    {{ getUserName(user) }}
                  </td>
                  <td>
                    <Chip :color="user.verified_at ? 'green' : 'gray'">
                      {{ user.email }}
                    </Chip>
                  </td>
                  <td>
                    {{
                      getFormattedDate(
                        getFieldValue(user, "createdAt", "created_at"),
                      )
                    }}
                  </td>
                  <td>
                    <div class="table-actions" @click.stop>
                      <button @click="copyUserURL(user)">
                        <img src="@/assets/icons/copy.svg" alt="" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </Table>
          <Pagination
            v-if="!searchQuery"
            :options="pagination.options"
            :meta="pagination.meta"
            @options-change="
              (newOptions) => {
                pagination.options = newOptions;
              }
            "
          />
        </div>
      </div>
    </div>
    <Transition name="fade" mode="out-in">
      <Modal
        v-if="
          isUserModalVisible &&
          selectedUserUuid &&
          (isUserLoading || !selectedUserData)
        "
        key="loading"
        @close="closeUserModal"
      >
        <LoadingLogo />
      </Modal>
      <UserModal
        v-else-if="isUserModalVisible && selectedUserUuid && selectedUserData"
        key="user-modal"
        :user="selectedUserData"
        @close="closeUserModal"
      />
    </Transition>
    <Transition name="fade">
      <Modal v-if="isActiveUsersModalOpened" @close="closeActiveUsersModal">
        <div class="active-users">
          <h2 class="active-users__heading">Активные пользователи</h2>
          <div class="active-users__loader" v-if="isActiveUsersLoading">
            <LoadingLogo />
          </div>
          <div class="active-users__list" v-else-if="activeUsers?.length">
            <UserListItem
              v-for="user in activeUsers"
              :key="user.uuid"
              :user="user"
              @click="selectUser(user)"
            />
          </div>
          <div class="active-users__empty" v-else>
            В данный момент активных пользователей нет.
          </div>
        </div>
      </Modal>
    </Transition>
  </section>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import copyToClipboard from "@/utils/copyToClipboard";
import getUserName from "@/utils/getUserName";
import getFormattedDate from "@/utils/getFormattedDate";
// eslint-disable-next-line no-unused-vars
import { getFieldValue } from "@/utils/getFieldValue";
import UserRoles from "@/utils/UserRoles";
import Modal from "@/components/Modal.vue";
import Header from "@/components/Header.vue";
import Search from "@/components/Search.vue";
import LoadingLogo from "@/components/LoadingLogo.vue";
import Pagination from "@/components/Pagination.vue";
import UserModal from "@/components/UserModal.vue";
import UserListItem from "@/components/UserListItem.vue";
import SelectButtons from "@/components/SelectButtons.vue";
import Table from "@/components/Table.vue";
import Chip from "@/components/Chip.vue";

export default {
  components: {
    Modal,
    Header,
    Search,
    LoadingLogo,
    Pagination,
    UserModal,
    UserListItem,
    SelectButtons,
    Table,
    Chip,
  },
  data() {
    return {
      UserRoles,

      isLoading: false,
      searchQuery: "",
      isInitialLoad: true, // Флаг для отслеживания первой загрузки

      selectedUserUuid: "",
      selectedUserData: null,
      isUserModalVisible: false,
      isUserLoading: false,
      isActiveUsersModalOpened: false,
      isActiveUsersLoading: false,

      searchUserLoading: false,
      searchUserPointer: false,
      searchedUser: [],

      verifiedDropdownItems: [
        {
          value: undefined,
          name: "Все",
          key: this.value,
        },
        {
          value: 1,
          name: "Верифицирован",
          key: this.value,
        },
        {
          value: 0,
          name: "Не верифицирован",
          key: this.value,
        },
      ],

      pagination: {
        options: {
          page: 1,
          take: 20,
          role: null,
          verified: null,
          order: "DESC",
        },
        meta: {},
      },
    };
  },
  mounted() {
    this.fetchUsersRequest();
    this.fetchUsersMetadata();
    this.selectUserFromUrl();
  },
  methods: {
    getFieldValue,
    ...mapActions([
      "fetchUsers",
      "fetchFile",
      "fetchUserPurchases",
      "fetchUsersMetadata",
      "fetchUser",
      "fetchActiveUsers",
      "fetchCredential",
      "fetchProfileByCredential",
      "searchUser",
    ]),

    ...mapMutations(["setUser"]),
    copyToClipboard,
    getUserName,
    getFormattedDate,

    copyUserURL(user) {
      navigator.clipboard.writeText(
        `${window.location.origin}/users/${user.uuid}`,
      );
    },

    selectUserFromUrl() {
      const uuid = this.$route.params.user_uuid;

      if (uuid) {
        // Сначала устанавливаем selectedUserUuid и открываем модальное окно,
        // чтобы оно не закрывалось во время загрузки данных
        this.selectedUserUuid = uuid;
        this.isUserModalVisible = true;
        this.isUserLoading = true;
        // Затем загружаем данные пользователя
        this.fetchUser(uuid)
          .then((user) => {
            // Сохраняем загруженного пользователя напрямую в data
            if (user && user.uuid) {
              this.selectedUserUuid = user.uuid;
              this.selectedUserData = user;
            }
            this.fetchUserPurchasesRequest(user.uuid);
          })
          .catch((error) => {
            console.error("Failed to fetch user:", error);
            // Если не удалось загрузить пользователя, закрываем модальное окно
            this.closeUserModal();
          })
          .finally(() => {
            this.isUserLoading = false;
          });
      }
    },

    toggleSort() {
      switch (this.pagination.options.order) {
        case undefined:
          this.pagination.options.order = "ASC";
          break;
        case "ASC":
          this.pagination.options.order = "DESC";
          break;
        case "DESC":
          this.pagination.options.order = undefined;
          break;
      }
    },

    closeActiveUsersModal() {
      this.isActiveUsersModalOpened = false;
    },

    openActiveUsersModal() {
      this.isActiveUsersModalOpened = true;
      this.isActiveUsersLoading = true;
      this.fetchActiveUsers().finally(() => {
        this.isActiveUsersLoading = false;
      });
    },

    closeUserModal() {
      this.isUserModalVisible = false;
      this.selectedUserUuid = "";
      this.selectedUserData = null;
      this.isUserLoading = false;
      this.$router.push({ name: "Users", params: { user_uuid: "" } });
    },

    selectUser(user) {
      this.$router.push({ name: "Users", params: { user_uuid: user.uuid } });
      this.closeActiveUsersModal();
      this.selectedUserUuid = user.uuid;
      this.selectedUserData = user;
      this.isUserModalVisible = true;
      this.fetchUserPurchasesRequest(user.uuid);
    },

    fetchUserPurchasesRequest(userUuid) {
      const purchases = this.userPurchases(userUuid);
      if (!purchases.length) {
        this.fetchUserPurchases(userUuid);
      }
    },

    async fetchUsersRequest() {
      this.isLoading = true;
      const payload = this.pagination.options;

      try {
        // При изменении фильтров заменяем список полностью, а не обновляем
        const response = await this.fetchUsers({
          pagination: payload,
          replace: true,
        });
        this.pagination.meta = response.data.meta;

        // Ждем, пока данные попадут в store
        await this.$nextTick();

        // Добавляем флаг активности для каждого пользователя в store
        this.users.forEach((user) => {
          user.isActive = this.isUserOnline(user);
        });

        // Загружаем аватары после того, как данные уже в store
        this.fetchUserAvatars();
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchUserAvatars() {
      // Используем пользователей из store, а не переданный параметр
      const users = this.users;
      for (const user of users) {
        if (user.avatar_is_loading === undefined) {
          user.avatar_is_loading = true;
        }
        const avatarUuid =
          user.profile?.avatar_url || user.profile?.avatarUrl;
        if (
          avatarUuid != null &&
          avatarUuid !== '' &&
          String(avatarUuid) !== 'null'
        ) {
          await this.fetchFile(avatarUuid);
        }
        user.avatar_is_loading = false;
        // Обновляем пользователя в store, сохраняя все его данные
        this.setUser({ ...user });
      }
    },

    isUserOnline(user) {
      if (!user || !user.email) {
        return false;
      }
      return this.getUsersMetadata?.sessions?.some((session) => {
        return session?.credential?.email === user.email;
      });
    },
  },
  computed: {
    ...mapGetters([
      "users",
      "file",
      "userPurchases",
      "getUsersMetadata",
      "activeUsers",
      "getUser",
    ]),

    roleFilterItems() {
      const userRolesList = Object.values(this.UserRoles);
      const items = userRolesList.map((role) => ({
        key: role.key,
        name: role.title,
        value: role.key,
      }));

      return items;
    },

    selectedUser() {
      if (!this.selectedUserUuid) return null;
      const user = this.getUser(this.selectedUserUuid);
      // Если пользователь не найден, пытаемся найти его по строковому сравнению
      if (!user) {
        return (
          this.users.find(
            (u) => String(u.uuid) === String(this.selectedUserUuid),
          ) || null
        );
      }
      return user;
    },

    filteredUsers() {
      if (this.searchQuery) {
        return this.searchedUser;
      } else {
        // Если нет поискового запроса, возвращаем всех пользователей из store
        // Store уже содержит отфильтрованные данные от бэкенда
        return this.users;
      }
    },

    numberOfUsers() {
      return this.getUsersMetadata?.credentials?.length ?? "0";
    },

    numberOfUsersOnline() {
      return this.getUsersMetadata?.sessions?.length ?? "0";
    },
  },
  watch: {
    "$route.params.user_uuid": {
      handler(newUuid, oldUuid) {
        // Открываем модальное окно при изменении user_uuid в URL
        if (newUuid && newUuid !== oldUuid) {
          // Не вызываем selectUserFromUrl, если модальное окно уже открыто для того же пользователя
          // и пользователь уже загружен. Это предотвращает повторное открытие при клике на пользователя в списке
          const isSameUser =
            this.selectedUserUuid === newUuid ||
            (this.selectedUserData && this.selectedUserData.uuid === newUuid);
          if (!this.isUserModalVisible || !isSameUser) {
            this.selectUserFromUrl();
          }
        } else if (!newUuid && oldUuid) {
          // Закрываем модальное окно, если user_uuid был удален из URL
          this.closeUserModal();
        }
      },
      immediate: false,
    },
    searchQuery() {
      this.searchUserLoading = true;
      window.clearTimeout(this.searchUserPointer);
      this.searchUserPointer = null;
      this.searchedUser = [];
      this.searchUserPointer = setTimeout(() => {
        this.searchUser(this.searchQuery)
          .then((response) => {
            this.searchedUser = [response.data];
          })
          .finally(() => {
            this.searchUserLoading = false;
          });
      }, 500);
    },
    "pagination.options": {
      deep: true,
      immediate: false,
      handler() {
        // Пропускаем вызов при первой инициализации
        if (this.isInitialLoad) {
          this.isInitialLoad = false;
          return;
        }

        // Вызываем запрос при любом изменении фильтров
        this.fetchUsersRequest();
      },
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/_shared.scss";

.loading {
  height: 68px;
  animation: loading 2s linear infinite alternate;
}

@keyframes loading {
  0% {
    background-color: #949494; /* начальный серый цвет */
  }
  100% {
    background-color: #3f4146; /* конечный синий цвет */
  }
}

.users {
  padding-top: 90px;
  color: $white;

  &__main {
    display: flex;
  }

  &__loading {
    flex: 1;
    height: 60vh;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 16px;
  }
}

.tools {
  padding-top: 30px;
  height: min-content;
  width: 480px;
  margin-right: 25px;

  .tool {
    margin-bottom: 20px;
  }

  &__filters {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 8px 0 24px;
  }
}
.users__list {
  flex-grow: 1;
  height: 100%;
  padding-top: 30px;
}
.stats {
  background: #3f4146;
  border-radius: 8px;
  padding: 24px 32px;
  display: flex;
  gap: 48px;
  justify-content: center;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;

    &--clickable {
      cursor: pointer;
      transition: 0.15s;

      &:active {
        transition: 0.15s;
        transform: scale(0.95);
      }
    }
  }

  &__title {
    font-size: 32px;
    font-weight: 700;
    margin: 0;
  }

  &__subtitle {
    font-size: 15px;
    font-weight: 500;
    color: #9c9c9c;
    margin: 0;
  }
}

.active-users {
  width: 800px;
  max-height: 600px;
  overflow-y: auto;
  margin: 16px;
  background: black;
  border-radius: 9px;
  padding: 25px;

  &__heading {
    padding-left: 16px;
    margin-bottom: 24px;
  }

  &__loader {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 48px 0;
  }

  &__list {
  }

  &__empty {
    width: 100%;
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 48px 0;
    font-size: 18px;
  }
}

.resize-enter-active,
.resize-leave-active {
  transition: all 0.3s;
}

.resize-enter-from,
.resize-leave-to {
  transform: scale(0.6);
  opacity: 0.7;
}
</style>
